/* @flow */
import PropTypes from 'prop-types';
import * as React from 'react';

import assertions from 'lib/assertions';
import AuthAction from 'auth/AuthActions';
import AuthenticationView from 'auth/components/AuthenticationView';
import Button from 'components/Button';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import Cookies from 'lib/Cookies';
import Copy from 'components/Copy';
import EventTracking from 'lib/EventTracking';
import FormElement from 'components/FormElement';
import Input from 'components/Input';
import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';
import PasswordStrengthField from 'components/PasswordStrengthField';
import TitleHeader from 'components/TitleHeader';
import ToastActions from 'actions/ToastActions';
import UserActions from 'user/UserActions';
import { RESET_PASSWORD_TOKEN_COOKIE, RESET_PASSWORD_CODE_COOKIE } from 'constants/AuthConstants';

type STATUS =
  | 'setup'
  | 'setup_failed'
  | 'setup_failed_no_cookies'
  | 'setup_failed_no_token_cookie'
  | 'password_form'
  | 'password_form_failed';

type State = {
  status: STATUS,
  canSubmit: boolean,
  passwordScore: ?number,
  password?: string,
};

function Help() {
  return (
    <p>
      If the problem persist, please{' '}
      <Link href={LinkConstants.CONTACT_FORM} target="_blank">
        contact us
      </Link>
      .
    </p>
  );
}

function Setup() {
  return <CenteredLoadingIndicator />;
}

function SetupFailed() {
  return (
    <div>
      <TitleHeader
        title="Oops, an unexpected error happened."
        subtext="We‘ve been notified about the problem."
      />
      <Help />
    </div>
  );
}

function SetupFailedNoCookies() {
  return (
    <div>
      <TitleHeader
        title="Oops, looks like cookies are disabled on your device."
        subtext="Please activate cookies or try the link on another device."
      />
      <Help />
    </div>
  );
}

function SetupFailedNoTokenCookie() {
  return (
    <div>
      <TitleHeader
        title="Oops, something went wrong."
        subtext="Please retry the password reset link in the email."
      />
      <Help />
    </div>
  );
}

function PasswordFormFailed() {
  return (
    <div className="ResetPasswordPage-failure">
      <TitleHeader
        title="Your link has expired."
        subtext="For security reason the reset link sent via email is only valid for a few hours."
      />
      <Copy align="center">
        <p>
          You can request a new link <Link to="/forgot_password">here</Link>.
        </p>
        <Help />
      </Copy>
    </div>
  );
}

class ResetPasswordPage extends React.Component<{}, State> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  state: State = {
    status: 'setup',
    canSubmit: false,
    passwordScore: null,
  };

  componentWillMount() {
    if (!Cookies.isEnabled()) {
      console.error('reset-password-page cookies disabled');
      this.setState({ status: 'setup_failed_no_cookies' });
      return;
    }

    if (!(Cookies.get(RESET_PASSWORD_TOKEN_COOKIE) || Cookies.get(RESET_PASSWORD_CODE_COOKIE))) {
      console.warn('reset-password-page missing reset token cookie');
      this.setState({ status: 'setup_failed_no_token_cookie' });
      return;
    }

    // signout the user, this is because the endpoint
    // completly ignores the token if the user is // authenticated. We want to make sure we set the password
    // for account clicked in the email and not another one.
    AuthAction.signoutWithoutRedirect()
      .then(() => this.setState({ status: 'password_form' }))
      .catch(error => {
        console.warn(error, error.stack);
        console.error('reset-password-page signoutWithoutRedirect error');
        this.setState({ status: 'setup_failed' });
      });
  }

  disableButton = () => {
    if (this.state.canSubmit) {
      this.setState({ canSubmit: false });
    }
  };

  enableButton = () => {
    if (!this.state.canSubmit) {
      this.setState({ canSubmit: true });
    }
  };

  submit = () => {
    const onSuccess = () => {
      ToastActions.showSuccessToast('Your password has been changed');
      if (this.state.passwordScore !== null) {
        this.trackScore();
      }
      this.context.router.push('/classes');
    };

    const onFailure = error => {
      if (!error || error.code !== 'invalid_token') {
        console.warn(error, error.stack);
        console.error('reset-password-page resetPassword submit error');
      }
      this.setState({ status: 'password_form_failed' });
    };

    if (!this.state.password) {
      console.error('ResetPasswordPage - tried to reset without entering a password');
      return;
    }

    UserActions.resetPassword(this.state.password).then(onSuccess, onFailure);
  };

  handleScoreUpdated = (passwordScore: number) => {
    this.setState({ passwordScore });
  };

  handlePasswordUpdated = (password: string) => {
    this.setState({ password });
  };

  trackScore = () => {
    const { passwordScore } = this.state;
    EventTracking.track('password_strength', {
      amount: passwordScore,
    });
  };

  renderPasswordForm = () => {
    return (
      <div>
        <TitleHeader title="Change your password" />
        <form
          onSubmit={e => {
            e.preventDefault();
            this.submit();
          }}>
          <PasswordStrengthField
            name="new_password"
            label="New password"
            onScoreUpdated={this.handleScoreUpdated}
            onPasswordUpdated={this.handlePasswordUpdated}
          />
          <FormElement
            label="New password confirmation"
            onValidChange={this.enableButton}
            onInvalidChange={this.disableButton}
            validate={e => {
              if (e.target.value !== this.state.password) {
                throw Error('Must be same as password');
              }
              assertions.notEmpty(e.target.value);
            }}
            element={Input}
            elementProps={{
              required: true,
              type: 'password',
              autoComplete: 'new-password',
              className: 'ResetPasswordPage-passwordConfirmation',
              name: 'password_confirmation',
            }}
          />
          <Button
            size="small"
            type="submit"
            className="ResetPasswordPage-submit"
            disabled={!this.state.canSubmit}
            eventElementName="ResetPasswordPage-Submit">
            Change my password
          </Button>
        </form>
      </div>
    );
  };

  render() {
    const renderFn = {
      setup: Setup,
      setup_failed: SetupFailed,
      password_form: this.renderPasswordForm,
      password_form_failed: PasswordFormFailed,
      setup_failed_no_cookies: SetupFailedNoCookies,
      setup_failed_no_token_cookie: SetupFailedNoTokenCookie,
    }[this.state.status];

    return <AuthenticationView className="ResetPasswordPage">{renderFn()}</AuthenticationView>;
  }
}

export default ResetPasswordPage;
